import { useEffect } from 'react'
import { message } from 'antd'
import { useDispatch } from 'src/hooks/useRedux'
import { setMeIntegrationEnabled } from '@sportninja/common/actions/account'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { HOCKEY_CANADA_MESSAGES } from '../constants'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { useHockeyCanadaReducer } from './useHockeyCanadaReducer'
import { useSetHockeyCanadaIntegration } from './useSetHockeyCanadaIntegration'
import type { HockeyCanadaIntegrationsSlice } from '../types'

interface HockeyCanadaIntegrationParams {
  accountUid?: string
  integrations?: HockeyCanadaIntegrationsSlice
}

interface HockeyCanadaIntegrationReturn {
  isHockeyCanadaConnected: boolean
  isHockeyCanadaEnabled: boolean
  showHockeyCanadaWarning: boolean
  isUpdatingHockeyCanada: boolean
  handleHockeyCanadaToggle: (enabled: boolean) => void
  confirmHockeyCanadaEnable: () => void
  cancelHockeyCanadaEnable: () => void
}

/**
 * Manages the account-level Hockey Canada integration.
 *
 * Enabling is gated behind a cost-confirmation modal; disabling is immediate.
 * The toggle is optimistic and rolls back if the request fails.
 *
 * No account refetch on success — the request already confirms the new state
 * (same as the USA Hockey card), and refetching caused a duplicate
 * GET /account/:id via MainNav's account-dependent effect.
 *
 * `account.integrations.hockeycanada.is_enabled` is `null` until the
 * integration has been configured, so it is coerced to a boolean here.
 */
export const useHockeyCanadaIntegration = ({
  accountUid,
  integrations,
}: HockeyCanadaIntegrationParams): HockeyCanadaIntegrationReturn => {
  const dispatch = useDispatch()
  const { state, actions } = useHockeyCanadaReducer()
  const { isConnected, isEnabled, showWarning, pendingEnabled } = state

  const { mutate: setIntegration, isPending } = useSetHockeyCanadaIntegration()

  const isEnabledFromApi = integrations?.hockeycanada?.is_enabled

  // Mirror server state into local state whenever the account payload changes.
  useEffect(() => {
    if (!accountUid) {
      return
    }

    const enabled = isEnabledFromApi === true
    actions.setStatus(enabled, enabled)
    // actions is recreated each render; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, isEnabledFromApi])

  /**
   * Persists the enabled state, rolling back the toggle if the request fails.
   */
  const updateStatus = (enabled: boolean) => {
    if (!accountUid || isPending) {
      return
    }

    // Optimistic: reflect the toggle immediately, revert on failure.
    const previousEnabled = isEnabled
    actions.setIsEnabled(enabled)

    setIntegration(
      { accountUid, enabled },
      {
        onSuccess: () => {
          // Server confirmed the change; settle local state on it and patch
          // the account slice so the value survives this card unmounting
          // (e.g. switching to another settings tab and back).
          actions.setStatus(enabled, enabled)
          dispatch(setMeIntegrationEnabled({ key: 'hockeycanada', enabled }))
          message.success(
            capitalizeWords(
              enabled
                ? HOCKEY_CANADA_MESSAGES.enabled
                : HOCKEY_CANADA_MESSAGES.disabled
            )
          )
        },
        onError: (error) => {
          // Roll the switch back, then surface why it failed. getErrorMessage
          // unwraps the API's own message and never returns empty, so it is
          // used directly rather than behind a local fallback.
          actions.setIsEnabled(previousEnabled)
          message.error(getErrorMessage(error))
        },
      }
    )
  }

  /**
   * Enabling requires confirmation (each validation incurs a cost);
   * disabling applies immediately.
   */
  const handleHockeyCanadaToggle = (enabled: boolean) => {
    if (enabled) {
      actions.setPendingEnabled(true)
      actions.setShowWarning(true)
      return
    }
    updateStatus(false)
  }

  const confirmHockeyCanadaEnable = () => {
    const enabled = pendingEnabled
    actions.resetWarningState()
    updateStatus(enabled)
  }

  const cancelHockeyCanadaEnable = () => {
    actions.resetWarningState()
  }

  return {
    isHockeyCanadaConnected: isConnected,
    isHockeyCanadaEnabled: isEnabled,
    showHockeyCanadaWarning: showWarning,
    isUpdatingHockeyCanada: isPending,
    handleHockeyCanadaToggle,
    confirmHockeyCanadaEnable,
    cancelHockeyCanadaEnable,
  }
}
